import React, { useState, useEffect } from 'react';
import { Copy, Check, Loader2, Gift, Send, Users } from 'lucide-react';
import { UserProfile } from '../types';
import { trackEvent } from '../services/analytics';
import { api } from '../services/api';

interface Referral {
  id: number;
  referee_id: number;
  credits_awarded: number;
  status: string;
  created_at: string;
  email: string;
  first_name: string | null;
}

interface SentInvite {
  id: number;
  recipient_email: string;
  recipient_name: string | null;
  type: string;
  status: string;
  created_at: string;
}

interface Props {
  user: UserProfile;
}

const fmt = (dt: string | null) => dt
  ? new Date(dt).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : '-';

export const ReferralsPage: React.FC<Props> = ({ user }) => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [sentInvites, setSentInvites] = useState<SentInvite[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get<Referral[]>('/api/referrals'),
      api.get<SentInvite[]>('/api/referrals/sent'),
    ])
      .then(([refs, invites]) => { setReferrals(refs); setSentInvites(invites); })
      .catch(() => {})
      .finally(() => setLoadingRefs(false));
  }, []);

  const referralUrl = `https://app.stagery.ai?ref=${user.referralCode}`;
  const copyLink = async () => {
    await navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    trackEvent('referral_link_copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const [invEmail, setInvEmail] = useState('');
  const [invName, setInvName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [invResult, setInvResult] = useState<string | null>(null);
  const [invError, setInvError] = useState<string | null>(null);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invEmail.trim()) return;
    setInviting(true);
    setInvError(null);
    setInvResult(null);
    try {
      await api.post('/api/referrals/invite', { email: invEmail.trim(), firstName: invName.trim() || undefined });
      setInvResult('Uitnodiging verstuurd!');
      trackEvent('referral_invite_sent');
      setInvEmail('');
      setInvName('');
      api.get<SentInvite[]>('/api/referrals/sent').then(setSentInvites).catch(() => {});
    } catch (err: unknown) {
      setInvError(err instanceof Error ? err.message : 'Er ging iets mis');
    } finally {
      setInviting(false);
    }
  };

  const activatedCount = referrals.filter(r => r.status === 'activated').length;
  const pendingCount = referrals.filter(r => r.status === 'pending').length;
  const earnedCredits = referrals.filter(r => r.status === 'activated').reduce((s, r) => s + r.credits_awarded, 0);

  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45 mb-2">Account</p>
          <h1 className="font-sans text-3xl md:text-4xl font-bold tracking-[-0.02em] text-ink">Referrals</h1>
          <p className="text-sm text-ink/55 mt-2 max-w-2xl">
            Nodig collega's uit en help hen sneller van start te gaan. Zij krijgen <strong className="text-ink">10 extra credits</strong> bij registratie,
            jij krijgt <strong className="text-ink">10 credits</strong> zodra zij hun eerste render maken.
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
          <StatCard icon={<Users size={15} />} label="Uitnodigingen" value={String(sentInvites.length)} />
          <StatCard icon={<Check size={15} />} label="Verzilverd" value={String(activatedCount)} sub={pendingCount ? `${pendingCount} wachtend` : undefined} />
          <StatCard icon={<Gift size={15} />} label="Verdiende credits" value={`+${earnedCredits}`} accent />
        </div>

        {/* Share link */}
        <section className="bg-white rounded-2xl border border-black/8 p-6 md:p-7 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-accent/10 text-accent flex items-center justify-center">
              <Gift size={15} />
            </div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.11em] text-ink/55">Geef 10, krijg 10</p>
          </div>
          <h2 className="font-sans text-xl font-bold tracking-[-0.015em] mb-2">Deel je referral-link</h2>

          {user.referralCode ? (
            <>
              <div className="flex flex-col sm:flex-row items-stretch gap-2 mt-4">
                <div className="flex-1 bg-bg border border-black/8 rounded-lg px-4 py-3 text-sm truncate text-ink/75 font-mono">
                  {referralUrl}
                </div>
                <button onClick={copyLink}
                  className="bg-accent hover:bg-accent-hover text-white px-5 py-3 rounded-lg font-semibold text-sm shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] hover:shadow-[0_10px_26px_-8px_rgba(241,110,0,0.55)] hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 shrink-0">
                  {copied ? <Check size={15} /> : <Copy size={15} />}
                  {copied ? 'Gekopieerd' : 'Kopieer link'}
                </button>
              </div>
              <p className="mt-3 text-[11px] text-ink/45 font-medium">
                Jouw code: <span className="text-ink/80 font-semibold">{user.referralCode}</span>
              </p>
            </>
          ) : (
            <p className="text-sm text-ink/45 mt-3">Geen referral code beschikbaar.</p>
          )}
        </section>

        {/* Invite form */}
        <section className="bg-white rounded-2xl border border-black/8 p-6 md:p-7 mb-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.11em] text-ink/55 mb-3">Stuur een directe uitnodiging</p>
          <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-2">
            <input
              type="text" value={invName} onChange={e => setInvName(e.target.value)}
              placeholder="Naam (optioneel)"
              className="border border-black/10 rounded-lg px-3.5 py-3 text-sm bg-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 sm:w-44"
            />
            <input
              type="email" value={invEmail} onChange={e => setInvEmail(e.target.value)}
              placeholder="collega@kantoor.nl" required
              className="border border-black/10 rounded-lg px-3.5 py-3 text-sm bg-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 flex-1"
            />
            <button type="submit" disabled={inviting}
              className="bg-ink hover:bg-ink/90 text-white px-5 py-3 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2 shrink-0 disabled:opacity-50">
              {inviting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Verstuur
            </button>
          </form>
          {invResult && <p className="text-xs text-accent mt-3 font-semibold">{invResult}</p>}
          {invError && <p className="text-xs text-red-600 mt-3">{invError}</p>}
        </section>

        {/* Sent invites */}
        {sentInvites.length > 0 && (
          <section className="bg-white rounded-2xl border border-black/8 p-6 md:p-7 mb-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.11em] text-ink/55 mb-4">
              Verstuurde uitnodigingen ({sentInvites.length})
            </p>
            <div className="divide-y divide-black/6">
              {sentInvites.map(inv => (
                <div key={inv.id} className="flex items-center gap-3 py-3">
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                    inv.status === 'registered' ? 'bg-accent' : 'bg-ink/20'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink truncate">{inv.recipient_name || inv.recipient_email}</p>
                    <p className="text-[11px] text-ink/50">{inv.recipient_email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`text-[10px] font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded-md ${
                      inv.status === 'registered' ? 'bg-accent/15 text-accent' : 'bg-ink/5 text-ink/55'
                    }`}>
                      {inv.status === 'registered' ? 'Geregistreerd' : 'Verstuurd'}
                    </span>
                    <p className="text-[10px] text-ink/40 mt-1">{fmt(inv.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Referral rewards list */}
        <section className="bg-white rounded-2xl border border-black/8 p-6 md:p-7">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.11em] text-ink/55">Referral status</p>
            {!loadingRefs && referrals.length > 0 && (
              <span className="text-[11px] text-ink/50 font-medium">
                {activatedCount} verzilverd · {pendingCount} wachtend
              </span>
            )}
          </div>
          {loadingRefs ? (
            <div className="text-xs text-ink/45 py-4"><Loader2 size={12} className="inline animate-spin mr-1.5" /> Laden…</div>
          ) : referrals.length === 0 ? (
            <p className="text-sm text-ink/45 py-3">Nog geen referrals. Deel je link hierboven!</p>
          ) : (
            <div className="divide-y divide-black/6">
              {referrals.map(r => (
                <div key={r.id} className="flex items-center gap-3 py-3">
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${r.status === 'activated' ? 'bg-accent' : 'bg-ink/20 animate-pulse'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink truncate">{r.first_name || r.email}</p>
                    <p className="text-[11px] text-ink/50">{fmt(r.created_at)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`text-[10px] font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded-md ${
                      r.status === 'activated' ? 'bg-accent/15 text-accent' : 'bg-ink/5 text-ink/55'
                    }`}>
                      {r.status === 'activated' ? `+${r.credits_awarded} credits` : 'Wachtend'}
                    </span>
                    {r.status === 'pending' && (
                      <p className="text-[10px] text-ink/40 mt-1">Eerste render nog niet gemaakt</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string; sub?: string; accent?: boolean }> = ({ icon, label, value, sub, accent }) => (
  <div className={`rounded-2xl border p-4 md:p-5 ${accent ? 'bg-accent/5 border-accent/20' : 'bg-white border-black/8'}`}>
    <div className="flex items-center gap-2 mb-2">
      <span className={`w-7 h-7 rounded-lg grid place-items-center ${accent ? 'bg-accent text-white' : 'bg-ink/5 text-ink/60'}`}>
        {icon}
      </span>
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink/55">{label}</span>
    </div>
    <p className={`font-sans text-2xl md:text-3xl font-bold tracking-[-0.015em] ${accent ? 'text-accent' : 'text-ink'}`}>{value}</p>
    {sub && <p className="text-[11px] text-ink/50 mt-1">{sub}</p>}
  </div>
);
